import { createSelector, MemoizedSelector } from '@ngrx/store';

import { Category } from '@fcom/dapi/api/models';
import { ServiceCatalogService } from '@fcom/dapi/interfaces';

import { BUNDLE_CATEGORY_ANCILLARY_MAP } from '../../modules/ancillaries/utils';
import type { CommonBookingFeatureState } from '../store.interface';
import { serviceCategoriesMapSelector } from './services.selector';
import { bundleCategoryToAncillaryVariantSelector } from './bundle.selector';

/**
 * Resolves the standalone ancillary service to display in the product-info modal for a given *seat bundle*
 * category (e.g. SEAT_MEAL → the MEAL service matching the bundle's variant). Seat bundles borrow their
 * modal content from the constituent ancillary's standalone catalog entry, so this bridges the bundle
 * variant mapping with the service catalog. Returns null when the bundle has no product-info modal or no
 * matching service is available.
 *
 * The modal derives everything it needs (including the ancillary category) from the returned service, so
 * the resolved service is all this selector exposes.
 *
 * Lives in its own file (rather than bundle.selector.ts) to avoid a cycle: it depends on both the service
 * catalog and bundle selectors, and services.selector already depends on bundle.selector.
 */
export const bundleProductInfoSelector = (
  bundleCategory: Category | null
): MemoizedSelector<CommonBookingFeatureState, ServiceCatalogService | null> =>
  createSelector(
    serviceCategoriesMapSelector,
    bundleCategoryToAncillaryVariantSelector,
    (categoriesMap, variantMap): ServiceCatalogService | null => {
      const ancillaryCategory = bundleCategory ? (BUNDLE_CATEGORY_ANCILLARY_MAP[bundleCategory] ?? null) : null;
      if (!ancillaryCategory) {
        return null;
      }
      const categoryServices = categoriesMap?.[ancillaryCategory];
      if (!categoryServices) {
        return null;
      }
      const allServices = Object.values(categoryServices)
        .flatMap((perFragment) => Object.values(perFragment))
        .flat();
      const variant = bundleCategory ? (variantMap[bundleCategory] ?? null) : null;
      return variant
        ? (allServices.find((service) => service.variant === variant) ?? allServices[0] ?? null)
        : (allServices[0] ?? null);
    }
  );
